<template>
  <node-view-wrapper class="vue-component">
    <div :class="['editor-image', selected ? '-selected' : '']">
      <div
        v-if="name === ''"
        class="_add-image"
        @click="showPrompt"
      >
        <div class="_add-image-inner">
          <div class="_icon">
            <c-icon
              type="file-image"
              size="22"
              :color="$colors.grey.base"
            />
          </div>
          <div class="_title">
            Add an image
          </div>
          <input
            id="file"
            ref="fileInput"
            class="_file"
            type="file"
            :style="{ display: 'none' }"
            name="avatar"
            accept="image/*"
            @change="fileChanged"
          >
        </div>
      </div>
      <div
        v-else-if="src === ''"
        class="_placeholder"
      >
        <div class="_icon">
          <c-icon
            type="file-image"
            size="22"
            :color="$colors.grey.base"
          />
        </div>
        <div class="_text">
          <div class="_name">
            {{ name }}
          </div>
          <div class="_size">
            {{ size }}
          </div>
          <div class="_progress">
            <c-icon
              type="loading"
              spin
              size="xxs"
              :color="$colors.grey.base"
              class="_spinner"
            />
            <div class="_progress-amount">
              {{ progress }} %
            </div>
          </div>
        </div>
      </div>
      <div
        v-else
        class="_inner"
      >
        <div
          class="_actions"
          :style="actionsStyle"
        >
          <c-button
            size="small"
            @click="openImage"
          >
            Original
          </c-button>
          <c-dropdown v-model:value="showMenu">
            <c-button
              size="small"
              fab
              icon="ellipsis"
              class="_btn"
              @click="showMenu = true"
            />
            <template #content>
              <c-card contains-list>
                <c-card-item
                  title="Delete"
                  icon="delete"
                  @click="deleteNode"
                />
                <a
                  :download="name"
                  :href="src"
                  :style="{ textDecoration: 'none' }"
                >
                  <c-card-item
                    icon="download"
                    title="Download"
                  />
                </a>
              </c-card>
            </template>
          </c-dropdown>
        </div>
        <img :src="src">
      </div>
    </div>
  </node-view-wrapper>
</template>

<script>
import { nodeViewProps, NodeViewWrapper } from '@tiptap/vue-3';
import CButton from 'shared/components/CButton.vue';
import CCard from 'shared/components/CCard.vue';
import CCardItem from 'shared/components/CCardItem.vue';
import CDropdown from 'shared/components/CDropdown.vue';
import CIcon from 'shared/components/CIcon.vue';
import { FILE_UPLOAD_LIMIT, FIREBASE_APP, USER_SERVICE } from 'shared/constants';
import useStorage from 'shared/services/storage';
import { inject } from 'vue';

export default {
  name: 'EditorImage',
  props: nodeViewProps,
  setup() {
    const firebaseApp = inject(FIREBASE_APP);
    const userService = inject(USER_SERVICE);

    const { uploadScreenshot } = useStorage(firebaseApp.storage, userService.currentUser);
    return { uploadFile: uploadScreenshot };
  },
  components: {
    CIcon,
    CDropdown,
    CButton,
    CCard,
    CCardItem,
    NodeViewWrapper,
  },
  data() {
    return {
      progress: 0,
      showMenu: false,
    };
  },
  computed: {
    view() {
      return this.editor.view;
    },
    actionsStyle() {
      if (this.showMenu) {
        return { display: 'flex' };
      }
      return {};
    },
    name: {
      get() {
        return this.node.attrs.name;
      },
      set({ name }) {
        this.updateAttributes({ name });
      },
    },
    size: {
      get() {
        if (this.node.attrs.size === null) {
          return '';
        }
        const size = this.node.attrs.size;
        if (size / 1000 < 1000) {
          return `${(size / 1000).toFixed(1)} kB`;
        }
        return `${(size / 1000000).toFixed(1)} MB`;
      },
      set(size) {
        this.updateAttributes({ size });
      },
    },
    src: {
      get() {
        return this.node.attrs.src;
      },
      set(src) {
        this.updateAttributes({ src });
      },
    },
    autoOpen: {
      get() {
        return this.node.attrs.autoOpen;
      },
      set(value) {
        this.udpateAttrs({ autoOpen: value });
      },
    },
    image() {
      return this.node.attrs.image;
    },
  },
  methods: {
    openImage() {
      window.open(this.src, '_blank');
    },
    showPrompt() {
      if (!this.view.editable) {
        return;
      }

      this.$refs.fileInput.click();
    },
    fileChanged(event) {
      if (event.target.files.length === 0) {
        return;
      }

      const file = event.target.files[0];
      if (file.size > FILE_UPLOAD_LIMIT) {
        this.$eventBus.emit('show-toast', {
          type: 'error',
          message: 'File too big',
        });
        return;
      }

      this.updateAttributes({ name: file.name });
      this.updateAttributes({ size: file.size });

      this.uploadFile(file, this.onUploadProgress).then(({ src }) => {
        this.updateAttributes({ src });
      }).catch(() => {
        this.$eventBus.emit('show-toast', {
          type: 'error',
          message: 'Something went wrong, please try again',
        });
      });
    },
    onUploadProgress(event) {
      this.progress = Math.round((event.loaded / event.total) * 100);
    },
  },
  mounted() {
    if (this.src !== '') {
      const url = new URL(this.src);
      if (url.host === this.$storage.host) {
        return;
      }
      fetch(this.src).then((res) => res.blob()).then((blob) => {
        this.uploadFile(blob).then(({ src }) => {
          this.updateAttributes({ src });
        });
      });
      return;
    }

    if (this.name === '') {
      if (this.autoOpen) {
        this.showPrompt();
        this.$nextTick(() => {
          this.updateAttributes({ autoOpen: false });
        });
      }
      return;
    }

    this.uploadFile(this.image).then(({ src }) => {
      this.updateAttributes({ src });
    });
  },
};
</script>

<style scoped lang="scss" type="text/scss">
@import "shared/assets/scss/box-shadow";

.editor-image {
  display: flex;
  justify-content: center;
  margin: 0.8rem 0;

  &.-selected {
    img {
      box-shadow: 0 0 0 3px map_get($blue, 'base');
    }
  }

  ._add-image {
    width: 100%;
    white-space: initial;
    background-color: map_get($grey, "lighten-5");
    border-radius: $border-radius;
    cursor: pointer;
    padding: 1rem 1.4rem;

    &:hover {
      background-color: map_get($grey, 'lighten-3');
    }

    ._add-image-inner {
      display: flex;
      align-items: center;
      height: 2.8rem;

      ._title {
        margin-left: 1.2rem;
        color: $font-color-secondary;
      }
    }
  }

  ._placeholder {
    width: 100%;
    white-space: initial;
    background-color: map_get($grey, "lighten-5");
    border-radius: $border-radius;
    display: flex;
    padding: 1rem 1.4rem;

    ._icon {
      margin-right: 1rem;
    }

    ._text {
      display: flex;
      align-items: baseline;
      width: 100%;

      ._name {
        max-width: calc(100% - 13rem);
        margin-right: 1rem;
        overflow: hidden;
        font-size: $font-size-5;
        font-weight: 500;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      ._size {
        font-size: $font-size-2;
        line-height: 1.5;
        color: $font-color-secondary;
      }

      ._progress {
        margin-left: 0.4rem;
        display: flex;
        align-items: center;
        font-size: $font-size-2;
        color: $font-color-secondary;
        align-self: center;

        ._spinner {
          margin-left: 1.2rem;
        }

        ._progress-amount {
          margin-left: .4rem;
        }
      }
    }
  }

  ._inner {
    position: relative;

    img {
      max-width: 100%;
    }

    ._actions {
      position: absolute;
      top: 0.6rem;
      right: 0.6rem;
      z-index: 99;
      display: none;
      background-color: white;
      border-radius: .4rem;
      padding: .2rem;

      @include box_shadow(1);

      ._btn {
        margin-left: 0.4rem;
      }
    }

    &:hover {
      ._actions {
        display: flex;
      }
    }
  }
}
</style>
